import {
  type ApartmentColumn,
  apartmentColumns,
  isAvailableColumn as isAvailable,
  parseSlotKey,
  type RestrictedAccess,
} from '@sivan/shared';
import { useCallback, useMemo } from 'react';
import type { SortColumn } from 'react-data-grid';
import {
  type CellFocusRequest,
  SheetGrid,
  type SheetColumnDef,
  type SheetGridAdapter,
} from './SheetGrid';
import {
  type SheetRow,
  changedFields,
  displayValue,
  isCleared,
  kindOf,
  lockedReason,
  rowState,
} from './apartment-draft';
import type { ClipboardTable } from './clipboard';
import { apartmentErrorText } from './labels';

const WIDTHS: Readonly<Record<string, number>> = {
  building: 72,
  floor: 96,
  apartmentNumber: 72,
  modelType: 120,
  sourceNumber: 112,
  sourceFloorLabel: 104,
  contactName: 170,
  contactPhone: 124,
  contactPhoneExtra: 124,
  contactEmail: 200,
  contactEmailExtra1: 200,
  contactEmailExtra2: 200,
  remarks: 240,
};
/** The apartment's identifier columns stay visible while scrolling sideways. */
const FROZEN = new Set(['building', 'floor', 'apartmentNumber']);

const widthOf = (column: ApartmentColumn): number => {
  const slot = parseSlotKey(column.key);
  if (slot === null) return WIDTHS[column.key] ?? 120;
  // Additional pairs carry longer headers ("קומת חניה נוספת 2").
  return slot.slot === 0 ? (slot.part === 'number' ? 96 : 104) : 128;
};

/** The 15 Tayasim columns, the source identity, then the additional pairs (BR-040, DR-183). */
export function apartmentSheetColumns(
  extra: {
    readonly parking: number;
    readonly storage: number;
  },
  access: RestrictedAccess = {},
): SheetColumnDef[] {
  return apartmentColumns(extra, access).map((c) => ({
    ...c,
    width: widthOf(c),
    frozen: FROZEN.has(c.key),
  }));
}

export interface ApartmentGridProps {
  readonly rows: readonly SheetRow[];
  readonly columns: readonly SheetColumnDef[];
  readonly focus?: CellFocusRequest | null;
  readonly canEdit: (row: SheetRow, field: string) => boolean;
  /** The restricted columns the reader may see: contacts (DR-185) and the note (DR-187). */
  readonly restricted: RestrictedAccess;
  readonly sortColumns: readonly SortColumn[];
  readonly onSortColumnsChange: (columns: SortColumn[]) => void;
  readonly onCellChange: (key: string, field: string, value: string) => void;
  readonly onCellClear: (key: string, field: string) => void;
  readonly onPaste: (table: ClipboardTable, anchorIndex: number, anchorColumn: number) => void;
  readonly onMessage: (message: string, tone: 'info' | 'error') => void;
  readonly onUndo: () => void;
  readonly onRedo: () => void;
  readonly onActiveRowChange?: (row: SheetRow | null) => void;
}

/** The Apartments sheet grid: the shared sheet grid with the Tayasim layout. */
export function ApartmentGrid(props: ApartmentGridProps) {
  const { canEdit, restricted } = props;
  const isAvailableColumn = useCallback(
    (key: string) => isAvailable(key, restricted),
    [restricted],
  );
  const adapter = useMemo(
    (): SheetGridAdapter<SheetRow> => ({
      key: (row) => row.key,
      display: displayValue,
      state: rowState,
      errors: (row) => row.errors,
      isCleared: (row, key) => isAvailableColumn(key) && isCleared(row, key),
      isChanged: (row, key) => changedFields(row).some((f) => f === key),
      locked: (row, key) => (isAvailableColumn(key) ? lockedReason(row, key) : null),
      base: (row, key) => row.base?.[key] ?? '',
      canEdit: (row, key) =>
        isAvailableColumn(key) && lockedReason(row, key) === null && canEdit(row, key),
      errorText: apartmentErrorText,
      clearedText: (key) => (kindOf(key) !== undefined ? 'ההצמדה תסתיים בשמירה' : 'יימחק בשמירה'),
    }),
    [canEdit, isAvailableColumn],
  );
  return (
    <SheetGrid
      label="גיליון דירות"
      rows={props.rows}
      columns={props.columns}
      focus={props.focus ?? null}
      adapter={adapter}
      sortColumns={props.sortColumns}
      onSortColumnsChange={props.onSortColumnsChange}
      onCellChange={(row, key, value) => {
        if (isAvailableColumn(key)) props.onCellChange(row.key, key, value);
      }}
      onCellClear={(row, key) => {
        if (isAvailableColumn(key)) props.onCellClear(row.key, key);
      }}
      onPaste={props.onPaste}
      onMessage={props.onMessage}
      onUndo={props.onUndo}
      onRedo={props.onRedo}
      {...(props.onActiveRowChange === undefined
        ? {}
        : { onActiveRowChange: props.onActiveRowChange })}
    />
  );
}
